import React, { useEffect, useMemo, useState } from "react";
import { onValue, ref, remove } from "firebase/database";
import { db } from "../../lib/firebase";
import "./FormSubmissions.css";

const getTimestamp = (submission) => {
  if (typeof submission?.createdAt === "number") return submission.createdAt;
  if (typeof submission?.createdAtClient === "number") return submission.createdAtClient;
  if (submission?.submittedAtIso) {
    const parsed = Date.parse(submission.submittedAtIso);
    if (!Number.isNaN(parsed)) return parsed;
  }
  return 0;
};

const toCsvSafe = (value) => {
  const normalized =
    value && typeof value === "object" ? JSON.stringify(value) : String(value ?? "");
  const escaped = normalized.replace(/"/g, "\"\"");
  return /[",\n]/.test(escaped) ? `"${escaped}"` : escaped;
};

const toDateTimeString = (timestamp) => {
  if (!timestamp) return "Unknown";
  return new Date(timestamp).toLocaleString();
};

const getFieldValue = (fields, keys) => {
  for (let i = 0; i < keys.length; i += 1) {
    const value = fields?.[keys[i]];
    if (value !== undefined && value !== null && String(value).trim() !== "") {
      return String(value);
    }
  }
  return "";
};

export default function FormSubmissions() {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [fromDateTime, setFromDateTime] = useState("");
  const [toDateTime, setToDateTime] = useState("");
  const [searchText, setSearchText] = useState("");
  const [deletingId, setDeletingId] = useState("");
  const [bulkDeleting, setBulkDeleting] = useState(false);

  useEffect(() => {
    const submissionsRef = ref(db, "formSubmissions");

    const off = onValue(
      submissionsRef,
      (snap) => {
        const value = snap.val();
        const rows = value
          ? Object.entries(value)
              .map(([id, data]) => {
                const timestamp = getTimestamp(data);
                return {
                  id,
                  ...(data || {}),
                  timestamp,
                };
              })
              .sort((a, b) => b.timestamp - a.timestamp)
          : [];

        setSubmissions(rows);
        setLoading(false);
      },
      (err) => {
        console.error(err);
        setError("Failed to load submissions.");
        setLoading(false);
      }
    );

    return () => off();
  }, []);

  const filteredSubmissions = useMemo(() => {
    const fromTs = fromDateTime ? new Date(fromDateTime).getTime() : null;
    const toTs = toDateTime ? new Date(toDateTime).getTime() : null;
    const query = searchText.trim().toLowerCase();

    return submissions.filter((submission) => {
      const timestamp = submission.timestamp || 0;

      if (Number.isFinite(fromTs) && fromTs !== null && timestamp < fromTs) {
        return false;
      }

      if (Number.isFinite(toTs) && toTs !== null && timestamp > toTs) {
        return false;
      }

      if (!query) return true;

      const searchSource = [
        submission.id,
        submission.formName,
        submission.formType,
        submission.sourcePath,
        JSON.stringify(submission.fields || {}),
      ]
        .join(" ")
        .toLowerCase();

      return searchSource.includes(query);
    });
  }, [fromDateTime, toDateTime, searchText, submissions]);

  const handleDeleteOne = async (id) => {
    if (!id) return;
    const ok = window.confirm("Delete this submission permanently?");
    if (!ok) return;

    setDeletingId(id);
    setError("");
    try {
      await remove(ref(db, `formSubmissions/${id}`));
    } catch (e) {
      console.error(e);
      setError("Failed to delete submission.");
    } finally {
      setDeletingId("");
    }
  };

  const handleDeleteFiltered = async () => {
    if (!filteredSubmissions.length) return;

    const ok = window.confirm(
      `Delete ${filteredSubmissions.length} filtered submission(s)? This cannot be undone.`
    );
    if (!ok) return;

    setBulkDeleting(true);
    setError("");
    try {
      await Promise.all(
        filteredSubmissions.map((submission) =>
          remove(ref(db, `formSubmissions/${submission.id}`))
        )
      );
    } catch (e) {
      console.error(e);
      setError("Some submissions could not be deleted.");
    } finally {
      setBulkDeleting(false);
    }
  };

  const handleExportCsv = () => {
    if (!filteredSubmissions.length) return;

    const fieldKeys = Array.from(
      new Set(
        filteredSubmissions.flatMap((submission) =>
          Object.keys(submission.fields || {})
        )
      )
    ).sort();

    const metadataKeys = Array.from(
      new Set(
        filteredSubmissions.flatMap((submission) =>
          Object.keys(submission.metadata || {})
        )
      )
    ).sort();

    const headers = [
      "submission_id",
      "submitted_at",
      "form_name",
      "form_type",
      "source_path",
      ...fieldKeys.map((key) => `field_${key}`),
      ...metadataKeys.map((key) => `meta_${key}`),
    ];

    const csvLines = [headers.map(toCsvSafe).join(",")];

    filteredSubmissions.forEach((submission) => {
      const row = [
        submission.id,
        submission.timestamp
          ? new Date(submission.timestamp).toISOString()
          : submission.submittedAtIso || "",
        submission.formName || "",
        submission.formType || "",
        submission.sourcePath || "",
        ...fieldKeys.map((key) => submission.fields?.[key] ?? ""),
        ...metadataKeys.map((key) => submission.metadata?.[key] ?? ""),
      ];
      csvLines.push(row.map(toCsvSafe).join(","));
    });

    const csv = `\uFEFF${csvLines.join("\n")}`;
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `submissions-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-")}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return <div className="submissions-shell">Loading submissions...</div>;
  }

  return (
    <div className="submissions-shell">
      <div className="submissions-toolbar">
        <div className="submissions-filters">
          <label className="submissions-label">
            From
            <input
              type="datetime-local"
              value={fromDateTime}
              onChange={(e) => setFromDateTime(e.target.value)}
            />
          </label>

          <label className="submissions-label">
            To
            <input
              type="datetime-local"
              value={toDateTime}
              onChange={(e) => setToDateTime(e.target.value)}
            />
          </label>

          <label className="submissions-label submissions-search">
            Search
            <input
              type="text"
              placeholder="Form, field, or value..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
          </label>
        </div>

        <div className="submissions-actions">
          <button type="button" onClick={handleExportCsv} disabled={!filteredSubmissions.length}>
            Download Excel (CSV)
          </button>
          <button
            type="button"
            className="danger"
            onClick={handleDeleteFiltered}
            disabled={!filteredSubmissions.length || bulkDeleting}
          >
            {bulkDeleting ? "Deleting..." : "Delete Filtered"}
          </button>
        </div>
      </div>

      <div className="submissions-meta">
        <span>Total: {submissions.length}</span>
        <span>Filtered: {filteredSubmissions.length}</span>
      </div>

      {error && <p className="submissions-error">{error}</p>}

      {!filteredSubmissions.length ? (
        <div className="submissions-empty">No submissions found for this filter.</div>
      ) : (
        <div className="submissions-table-wrap">
          <table className="submissions-table">
            <thead>
              <tr>
                <th>Date & Time</th>
                <th>Form</th>
                <th>Name</th>
                <th>Email / Phone</th>
                <th>Details</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {filteredSubmissions.map((submission) => {
                const fields = submission.fields || {};
                const name = getFieldValue(fields, ["name", "full_name"]);
                const email = getFieldValue(fields, ["email", "email_address"]);
                const phone = getFieldValue(fields, ["phone", "phone_number", "mobile"]);

                return (
                  <tr key={submission.id}>
                    <td>{toDateTimeString(submission.timestamp)}</td>
                    <td>
                      <div className="submissions-form-name">
                        {submission.formName || "Untitled Form"}
                      </div>
                      <div className="submissions-form-type">{submission.formType || "form"}</div>
                    </td>
                    <td>{name || "-"}</td>
                    <td>
                      <div>{email || "-"}</div>
                      <div>{phone || "-"}</div>
                    </td>
                    <td>
                      <details>
                        <summary>View</summary>
                        <pre>{JSON.stringify(fields, null, 2)}</pre>
                      </details>
                    </td>
                    <td>
                      <button
                        type="button"
                        className="danger"
                        onClick={() => handleDeleteOne(submission.id)}
                        disabled={deletingId === submission.id}
                      >
                        {deletingId === submission.id ? "Deleting..." : "Delete"}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
